import { useState, useRef } from "react";
import CommentThread from "../shared/CommentThread";

// ─── Constants ───────────────────────────────────────────────────────────────

export const TASK_STATUS_OPTS = ["ACTIVE","IN-PROGRESS","DONE","SKIPPED","CANCELLED"];

export const TASK_KANBAN_COLS = [
  { status: "ACTIVE",      label: "Todo",        color: "#6366f1" },
  { status: "IN-PROGRESS", label: "In Progress",  color: "#3b82f6" },
  { status: "DONE",        label: "Done",         color: "#22c55e" },
  { status: "SKIPPED",     label: "Skipped",      color: "#6b7280" },
  { status: "CANCELLED",   label: "Cancelled",    color: "#ef4444" },
];

const PRIORITY_META = {
  CRITICAL: { label: "Critical", color: "#ef4444" },
  HIGH:     { label: "High",     color: "#f97316" },
  MEDIUM:   { label: "Medium",   color: "#eab308" },
  LOW:      { label: "Low",      color: "#22c55e" },
  SOMEDAY:  { label: "Someday",  color: "#6b7280" },
};

const RECURRENCE_LABELS = {
  WEEKLY: "🔁 Weekly", MONTHLY: "🔁 Monthly",
  "HALF-YEARLY": "🔁 Half-yearly", YEARLY: "🔁 Yearly",
};

const TYPE_LABELS = {
  PAYMENT: "💳 Payment", REMINDER: "🔔 Reminder", IMPORTANT_DATE: "📌 Date",
};

function fmt(dt) {
  if (!dt) return "—";
  return new Date(dt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}
function daysFrom(dateStr) {
  if (!dateStr) return null;
  const diff = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
  if (diff === 0) return "today";
  if (diff > 0) return `in ${diff}d`;
  return `${Math.abs(diff)}d ago`;
}

// ─── Task Row ─────────────────────────────────────────────────────────────────

export function TaskRow({ task: t, projectColor, isDragOver, storyLabel, onStatusChange, onEdit, onDelete,
                   onDragStart, onDragOver, onDrop, onDragEnd }) {
  const isDone = t.status === "DONE" || t.status === "SKIPPED";
  const isOverdue = t.dueDate && t.dueDate < new Date().toISOString().slice(0, 10) && !isDone;
  const pMeta = PRIORITY_META[t.priority];
  const recurrenceLabel = RECURRENCE_LABELS[t.recurrence];
  const typeLabel = TYPE_LABELS[t.type];
  const [showComments, setShowComments] = useState(false);
  const [commentCount, setCommentCount] = useState(t.commentCount ?? 0);

  return (
    <div className="proj-task-row-wrap">
      <div
        className={`proj-task-row ${isDone ? "proj-task-done" : ""} ${isOverdue ? "proj-task-overdue" : ""} ${isDragOver ? "proj-task-drag-over" : ""}`}
        draggable
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onDragEnd={onDragEnd}
      >
        <span className="proj-task-drag-handle" title="Drag to reorder">⠿</span>
        <input
          type="checkbox"
          className="proj-task-check"
          checked={isDone}
          onChange={() => onStatusChange(isDone ? "ACTIVE" : "DONE")}
          style={{ accentColor: projectColor || "#6366f1" }}
        />
        <div className="proj-task-body">
          <div className="proj-task-title">{t.title}</div>
          {t.description && (
            <div className="proj-task-desc">{t.description}</div>
          )}
          <div className="proj-task-meta">
            {t.dueDate && (
              <span className={`proj-task-due ${isOverdue ? "overdue" : ""}`}>
                📅 {fmt(t.dueDate)}{isOverdue ? ` · ${Math.abs(Math.ceil((new Date(t.dueDate) - new Date()) / 86400000))}d overdue` : ` · ${daysFrom(t.dueDate)}`}
              </span>
            )}
            {t.startTime && (
              <span className="proj-task-chip">⏰ {t.startTime}{t.endTime ? ` – ${t.endTime}` : ""}</span>
            )}
            {t.category && (
              <span className="proj-task-chip">📂 {t.category}</span>
            )}
            {storyLabel && (
              <span className="proj-task-chip" title="Epic / Story">
                🧩 {storyLabel.epicName} / 📖 {storyLabel.storyName}
              </span>
            )}
            {recurrenceLabel && (
              <span className="proj-task-chip">{recurrenceLabel}</span>
            )}
            {typeLabel && (
              <span className="proj-task-chip">{typeLabel}</span>
            )}
            {pMeta && (
              <span className="proj-task-priority" style={{ background: pMeta.color + "22", color: pMeta.color, borderColor: pMeta.color + "44" }}>
                {pMeta.label}
              </span>
            )}
            <button
              type="button"
              className={`proj-task-chip proj-task-comment-badge ${showComments ? "proj-task-comment-badge-active" : ""}`}
              onClick={e => { e.stopPropagation(); setShowComments(s => !s); }}
              title="Comments"
            >
              💬 {commentCount}
            </button>
          </div>
        </div>
        <div className="proj-task-actions">
          <select
            className="proj-task-status-sel"
            value={t.status}
            onChange={e => onStatusChange(e.target.value)}
          >
            {TASK_STATUS_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button className="proj-task-action-btn" onClick={onEdit} title="Edit task">✏️</button>
          <button className="proj-task-action-btn proj-task-del-btn" onClick={onDelete} title="Delete task">🗑</button>
        </div>
      </div>
      {showComments && (
        <div className="proj-task-comments">
          <CommentThread compact entityType="TASK" entityId={t.id} onCountChange={setCommentCount} />
        </div>
      )}
    </div>
  );
}

// ─── Task Kanban Board ────────────────────────────────────────────────────────

export function TaskKanbanBoard({ tasks, projectColor, onStatusChange, onEdit, onDelete }) {
  const draggingId = useRef(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  return (
    <div className="proj-task-board">
      {TASK_KANBAN_COLS.map(col => {
        const colTasks = tasks.filter(t => t.status === col.status);
        return (
          <div
            key={col.status}
            className={`proj-task-kb-col ${dragOverCol === col.status ? "proj-task-kb-col-over" : ""}`}
            style={{ "--col-color": col.color }}
            onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverCol(col.status); }}
            onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverCol(null); }}
            onDrop={e => {
              e.preventDefault();
              setDragOverCol(null);
              if (draggingId.current) {
                const task = tasks.find(t => t.id === draggingId.current);
                if (task && task.status !== col.status) onStatusChange(task.id, col.status);
                draggingId.current = null;
              }
            }}
          >
            <div className="proj-task-kb-col-header">
              <span className="proj-task-kb-col-dot" />
              <span className="proj-task-kb-col-label">{col.label}</span>
              <span className="proj-task-kb-col-count">{colTasks.length}</span>
            </div>
            <div className="proj-task-kb-col-body">
              {colTasks.length === 0 && (
                <div className="proj-task-kb-empty">Drop tasks here</div>
              )}
              {colTasks.map(t => (
                <TaskKanbanCard
                  key={t.id}
                  task={t}
                  projectColor={projectColor}
                  onDragStart={() => { draggingId.current = t.id; }}
                  onEdit={() => onEdit(t)}
                  onDelete={() => onDelete(t.id)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function TaskKanbanCard({ task: t, projectColor, onDragStart, onEdit, onDelete }) {
  const isDone = t.status === "DONE" || t.status === "SKIPPED";
  const isOverdue = t.dueDate && t.dueDate < new Date().toISOString().slice(0, 10) && !isDone;
  const pMeta = PRIORITY_META[t.priority];

  return (
    <div
      className={`proj-task-kb-card ${isDone ? "proj-task-kb-done" : ""} ${isOverdue ? "proj-task-kb-overdue" : ""}`}
      draggable
      onDragStart={e => { e.dataTransfer.effectAllowed = "move"; onDragStart(); }}
    >
      <div className="proj-task-kb-title-row">
        {pMeta && (
          <span
            className="proj-task-kb-priority-dot"
            style={{ background: pMeta.color }}
            title={pMeta.label}
          />
        )}
        <span className="proj-task-kb-title">{t.title}</span>
      </div>
      {t.description && (
        <div className="proj-task-kb-desc">{t.description}</div>
      )}
      <div className="proj-task-kb-footer">
        {t.dueDate && (
          <span className={`proj-task-kb-due ${isOverdue ? "overdue" : ""}`}>
            📅 {fmt(t.dueDate)}
          </span>
        )}
        {pMeta && (
          <span
            className="proj-task-kb-prio"
            style={{ color: pMeta.color, background: pMeta.color + "18" }}
          >
            {pMeta.label}
          </span>
        )}
        {t.category && <span className="proj-task-kb-chip">{t.category}</span>}
        {t.commentCount > 0 && <span className="proj-task-kb-chip">💬 {t.commentCount}</span>}
      </div>
      <div className="proj-task-kb-actions">
        <button className="proj-task-kb-btn" onClick={onEdit} title="Edit task">✏️</button>
        <button className="proj-task-kb-btn proj-task-kb-del" onClick={onDelete} title="Delete task">🗑</button>
      </div>
    </div>
  );
}

// ─── Quick Task Form Modal ────────────────────────────────────────────────────

// `storyId` (fixed) is passed when creating a task from inside a specific
// Story — the picker is hidden and every new task is force-linked there.
// `epics` (a list of epics, each with its own `stories` array) is passed
// from the flat Project Tasks tab instead — it shows a picker so existing
// (including pre-Epic/Story-era) tasks can be assigned/reassigned/unassigned.
export function TaskFormModal({ projectName, task, storyId, epics, onSave, onClose }) {
  const isEdit = !!task;
  const [form, setForm] = useState({
    title:       task?.title       || "",
    description: task?.description || "",
    dueDate:     task?.dueDate     || "",
    startTime:   task?.startTime   || "",
    endTime:     task?.endTime     || "",
    priority:    task?.priority    || "MEDIUM",
    category:    task?.category    || "",
    storyId:     task?.storyId     || "",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    const { storyId: pickedStoryId, ...rest } = form;
    onSave({
      ...rest,
      dueDate:   form.dueDate   || null,
      startTime: form.startTime || null,
      endTime:   form.endTime   || null,
      category:  form.category  || null,
      ...(storyId
        ? { storyId }                                // locked — always this Story
        : epics
          ? { storyId: pickedStoryId || null }        // free picker — assign/move/unassign
          : {}),
    });
  };

  return (
    <div className="proj-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="proj-modal">
        <div className="proj-modal-header">
          <h3>{isEdit ? "Edit Task" : `New Task — ${projectName}`}</h3>
          <button className="proj-modal-close" onClick={onClose}>✕</button>
        </div>
        <form className="proj-modal-body" onSubmit={handleSubmit}>
          <div className="proj-form-group">
            <label>Task title *</label>
            <input className="proj-input" required autoFocus value={form.title}
                   onChange={e => set("title", e.target.value)}
                   placeholder="What needs to be done?" />
          </div>
          <div className="proj-form-group">
            <label>Description</label>
            <textarea className="proj-textarea" rows={3} value={form.description}
                      onChange={e => set("description", e.target.value)}
                      placeholder="Add details, context, or acceptance criteria…" />
          </div>
          <div className="proj-form-row">
            <div className="proj-form-group" style={{ flex: 1 }}>
              <label>Due Date</label>
              <input type="date" className="proj-input" value={form.dueDate}
                     onChange={e => set("dueDate", e.target.value)} />
            </div>
            <div className="proj-form-group" style={{ flex: 1 }}>
              <label>Priority</label>
              <select className="proj-select" value={form.priority}
                      onChange={e => set("priority", e.target.value)}>
                {["CRITICAL","HIGH","MEDIUM","LOW","SOMEDAY"].map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div className="proj-form-row">
            <div className="proj-form-group" style={{ flex: 1 }}>
              <label>Start time</label>
              <input type="time" className="proj-input" value={form.startTime}
                     onChange={e => set("startTime", e.target.value)} />
            </div>
            <div className="proj-form-group" style={{ flex: 1 }}>
              <label>End time</label>
              <input type="time" className="proj-input" value={form.endTime}
                     onChange={e => set("endTime", e.target.value)} />
            </div>
          </div>
          <div className="proj-form-group">
            <label>Category</label>
            <input className="proj-input" value={form.category}
                   onChange={e => set("category", e.target.value)}
                   placeholder="e.g. Backend, Design, Research…" />
          </div>
          {!storyId && epics && (
            <div className="proj-form-group">
              <label>Epic / Story</label>
              <select className="proj-select" value={form.storyId}
                      onChange={e => set("storyId", e.target.value)}>
                <option value="">— No Epic / Story (flat project task) —</option>
                {epics.map(ep => (
                  <optgroup key={ep.id} label={`🧩 ${ep.name}`}>
                    {(ep.stories || []).map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          )}
          <div className="proj-modal-footer">
            <button type="button" className="proj-btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="proj-btn-primary">{isEdit ? "Save Changes" : "Add Task"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
